import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Briefcase, Banknote, Building2 } from "lucide-react";
import { Card } from "./ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function JobCard({ job, showStats = false, showMatchScore = false }) {
  const navigate = useNavigate();
  const fmt = (n) => n ? new Intl.NumberFormat("en-MU").format(n) : "";
  const logo = job.company_logo_url;
  const logoUrl = logo ? (logo.startsWith("http") ? logo : `${BACKEND_URL}${logo}`) : null;
  const url = job.slug_prefix && job.slug_suffix ? `/jobs/${job.slug_prefix}/${job.slug_suffix}` : `/jobs/${job.id}`;

  const goToCareer = (e) => {
    const key = job.employer_slug || job.employer_id;
    if (!key) return;
    e.preventDefault();
    e.stopPropagation();
    navigate(`/career/${key}`);
  };

  return (
    <Link to={url} data-testid={`job-card-${job.id}`} className="block group">
      <Card className="p-5 hover:border-[#1A8FE3] hover:shadow-md transition-all duration-200 bg-white border-slate-200">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
            {logoUrl ? (
              <img src={logoUrl} alt={`${job.company_name} logo`} className="w-full h-full object-contain p-1" />
            ) : (
              <Building2 className="h-5 w-5 text-slate-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-display text-lg font-semibold text-slate-900 group-hover:text-[#1A8FE3] transition-colors line-clamp-2">{job.title}</h3>
                {(job.employer_slug || job.employer_id) ? (
                  <button
                    type="button"
                    onClick={goToCareer}
                    data-testid={`job-card-company-${job.id}`}
                    className="text-sm text-slate-500 mt-0.5 hover:text-[#1A8FE3] hover:underline text-left"
                  >
                    {job.company_name}
                  </button>
                ) : (
                  <p className="text-sm text-slate-500 mt-0.5">{job.company_name}</p>
                )}
              </div>
              {job.contract_type && (
                <span className="shrink-0 text-xs font-medium px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
                  {job.contract_type}
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" />{job.location}</span>
              {job.sector && <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-slate-400" />{job.sector}</span>}
              {job.salary_min && (
                <span className="inline-flex items-center gap-1.5"><Banknote className="h-3.5 w-3.5 text-slate-400" />Rs {fmt(job.salary_min)}{job.salary_max ? ` - ${fmt(job.salary_max)}` : ""}</span>
              )}
            </div>
            {(showMatchScore && typeof job.match_score === "number") && (
              <div className="mt-3 inline-flex items-center gap-2 text-xs">
                <span className="font-semibold text-[#1A8FE3]">Match {job.match_score}%</span>
                {job.match_reason && <span className="text-slate-500 line-clamp-1">· {job.match_reason}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-500">
          {showStats && (
            <>
              <span>{job.views || 0} views</span>
              <span>{job.applications_count || 0} applications</span>
            </>
          )}
          <span className="ml-auto">{new Date(job.created_at).toLocaleDateString()}</span>
        </div>
      </Card>
    </Link>
  );
}
